import i18n from '@/i18n'
import { filterRulesMapped } from '@/tools/filterRules'
import { userService } from '@/services/user.service'
import { enterpriseService } from '@/services/enterprise.service'
import { mapActions, mapGetters } from 'vuex'
import { DialogType, SnackbarStyle } from '@/constants/constants'
import { dialogTypes } from "@/constants/dialogTypes"
import UserDialogComponent from "@/components/user/dialog/UserDialogComponent.vue"
import { DialogUser, dialogUtil } from "@/tools/dialog"
import { clone } from 'ramda'
import Vue from 'vue'
import ProfileDialogComponent from "@/components/profile/dialog/ProfileDialogComponent.vue"
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
    name: 'UserABMComponent',
    components: {
        UserDialogComponent,
        ProfileDialogComponent,
        AutocompleteComponent
    },
    data: () => ({
        // headers
        tableHeaders: [],
        // reglas de filtrado
        tableFilterRules: {},
        // datos
        tableData: [],
        // key de los dato de la tabla
        tableDataKey: '_id',
        // botones de cada fila
        rowActions: [
            {
                id: 1,
                name: i18n.t('modify'),
                color: 'blue',
                icon: 'fa-pen'
            },
            {
                id: 2,
                name: i18n.t('delete'),
                color: 'red',
                icon: 'fa-trash-alt'
            },
            {
                id: 3,
                name: i18n.t('user.disableUser'),
                color: 'orange',
                icon: 'mdi-account-lock'
            },
            // usar como plantilla
            {
                id: 4,
                name: i18n.t('template'),
                color: 'green',
                icon: 'fa-clone'
            }
        ],
        // botones del componente Tabla
        tableComponentButtons: [],
        // tamaño de página
        pageSize: undefined,
        // núm de página
        pageNumber: undefined,
        // total de elementos de la base de datos
        pageTotal: 0,
        // dialog de edición
        dialog: {
            ...dialogTypes.USER
        },
        // model del dialog
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('user.editDialog.titleNew'),
        // ícono del dialog
        editDialogIcon: 'mdi-account-plus',
        // loading de la tabla
        loadingTable: true,
        // arreglo de empresas del selector
        enterprises: [],
        // empresa seleccionada
        enterpriseSelected: null,
        // Opción 'TODOS' para el selector de empresa
        all: {
            _id: 'ALL',
            name: i18n.t('all')
        },
        // para deshabilitar el botón de creación cuando no está seleccionada una empresa
        disableAddButton: false,
        isClone: false
    }),
    computed: {
        /**
         * getters del breadcrumb
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ]),
        /**
         * Valor computado para hacer las requests al api
         */
        enterpriseSelectedArray() {
            if (this.enterpriseSelected) {
                if (this.enterpriseSelected === this.all._id) {
                    return this.enterprises.filter(enterprise => enterprise._id !== this.all._id)
                        .map(enterprise => enterprise._id)
                } else {
                    return [this.enterpriseSelected]
                }
            } else {
                return []
            }
        },
        /**
         * Para incluir usuarios sin empresa asignada en la tabla
         */
        enterpriseLess() {
            return (!this.enterpriseSelected || this.enterpriseSelected === this.all._id)
        }
    },
    created() {
        /*
            Vuex watch del breadcrumb
        */
        this.unwatch = this.$store.watch(
        (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastElemet = newBreads[newBreads.length - 1]
                if (!lastElemet) {
                    this.cancel()
                    return
                }
                this.dialog = dialogUtil.getDialog(lastElemet.key, DialogUser)
            },
        { deep: true })
    },
    beforeDestroy() {
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
        this.cleanBread()
    },
    mounted() {
        // inicialización de los datos del ABM
        this.initialize()
    },
    methods: {
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pushBread',
            'cleanBread'
        ]),
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * Para inicializar el componente
         */
        async initialize () {
            // Botón de eliminación múltiple
            this.tableComponentButtons.push(
                {
                    id: 'edit-dialog',
                    tooltip: i18n.t('add'),
                    icon: 'add',
                    action: this.showCreateDialog.bind(this),
                    selectedData: false
                },
                {
                    id: 'multiple-disable',
                    tooltip: i18n.t('user.disableUsers'),
                    icon: 'mdi-account-lock',
                    action: this.multipleDisable.bind(this),
                    selectedData: true
                },
                {
                    id: 'multiple-delete',
                    tooltip: i18n.t('multipleDelete'),
                    icon: 'delete_outline',
                    action: this.multipleDelete.bind(this),
                    selectedData: true
                })

            // empresas
            await this.initEnterpriseSelector()

            // total de líneas (para el paginador)
            this.pageTotal = await userService.getTotal(this.enterpriseSelectedArray, this.enterpriseLess)

            // Si hay query params en la URL, los setea para incluirlos en la llamada al backend
            if (this.$route.query.pageSize && this.$route.query.pageNumber) {
                this.pageSize = Number(this.$route.query.pageSize)
                this.pageNumber = Number(this.$route.query.pageNumber)

                // Valida que los qery params sean correctos y completos
                if ((this.pageSize && this.pageSize <= 0) || (this.pageNumber && this.pageNumber <= 0) || (this.pageNumber && !this.pageSize) || (!this.pageNumber && this.pageSize)) {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('error.invalidQueryParams'),
                        timeout: 6000,
                        style: SnackbarStyle.ERROR
                    })
                    this.pageSize = this.pageTotal
                    this.pageNumber = 1
                }
            } else {
                this.pageSize = 50
                this.pageNumber = 1
            }

            // cargo la tabla
            this.getData()
            this.resetFields()
            this.initBread()
        },
        /**
         * Inicializa el selector de empresas
         */
        async initEnterpriseSelector() {
            this.enterprises = await enterpriseService.getAllForSelector()
            if (this.enterprises.length > 1) {
                this.enterprises = ([this.all]).concat(this.enterprises)
                this.enterpriseSelected = this.all._id
            } else if (this.enterprises.length === 1) {
                this.enterpriseSelected = this.enterprises[0]._id
            }
        },
        /**
         * Obtiene los datos para la tabla del ABM
         * @param {*} update
         */
        async getData(update = false) {
            this.loadingTable = true
            if (update) {
                this.pageTotal = await userService.getTotal(this.enterpriseSelectedArray, this.enterpriseLess)
                this.pageSize = 50
                this.pageNumber = 1
            }
            const data = await userService.findAll(this.pageSize, this.pageNumber, !update, this.enterpriseSelectedArray, this.enterpriseLess)
            const lastTableHeaders = clone(this.tableHeaders)
            this.tableData.cleanAndUpdate(data.data)
            if (update) {
                const that = this
                Vue.nextTick(function () {
                    that.tableHeaders.cleanAndUpdate(lastTableHeaders)
                })
            } else {
                this.tableHeaders.cleanAndUpdate(data.headers)
                this.setTableFilterRules()
            }
            this.loadingTable = false
        },
        /**
         * Refrescar la data
         */
        refreshTable() {
            this.getData(true)
        },
        /**
         * Setea las reglas de filtrado
         */
        setTableFilterRules() {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Inicializa el breadcrumb del dialog
         */
        initBread() {
            const bread = {
                key: DialogUser,
                disabled: true,
                text: this.$t('user.name')
            }
            this.pushBread(bread)
        },
        /**
         * Para eliminar una línea
         * @param {*} data 
         */
        async delete(data) {
            userService.deleteOne(data._id).then(() => {
                this.showSnackbar({
                    visible: true,
                    text: this.$t('user.successDeleteUserMessage'),
                    timeout: 6000,
                    style: SnackbarStyle.SUCCESS
                })
                this.getData(true)
            })
        },
        /**
         * Para eliminación múltiple
         * @param {*} data
         */
        async multipleDelete(data) {
            this.openDialog({
                title: this.$t('user.multipleDeleteTitle'),
                text: this.$t('user.multipleDeleteMessage'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: async () => {
                    const ids = data.map(e => e._id)
                    userService.deleteMany(ids).then(() => {
                        this.showSnackbar({
                            visible: true,
                            text: this.$t('user.successDeleteMessage'),
                            timeout: 6000,
                            style: SnackbarStyle.SUCCESS
                        })
                        this.getData(true)
                    })
                },
                noAction: () => {
                }
            })
        },
        /**
         * Para deshabilitar múltiples usuarios
         * @param {*} data
         */
        async multipleDisable(data) {
            this.openDialog({
                title: this.$t('user.disableUsers'),
                text: this.$t('user.disableUsersText'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: async () => {
                    this.disable(data)
                },
                noAction: () => {
                }
            })
        },
        /**
         * Carga los datos para el modal de edición
         * @param {*} id
         */
        async loadFields(id) {
            const user = await userService.getUserForABM(id)
            if (user) {
                this.dialog.model.isClone = this.isClone
                const modelData = this.dialog.model
                Object.assign(modelData, user)
                this.showUpdateDialog()
            } else {
                this.showSnackbar({
                    visible: true,
                    text: this.$t('user.error.couldNotLoadUser'),
                    timeout: 6000,
                    style: SnackbarStyle.ERROR
                })
            }
        },
        rowButtonClicked(button, data) {
            switch (button.id) {
                case 1:
                    // TO DO: agregar el id como query param
                    this.isClone = false
                    this.loadFields(data._id)
                    break
                case 2:
                    this.openDialog({
                        title: this.$t('user.deleteTitle'),
                        text: this.$t('user.deleteText', { username: data.username }),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.delete(data)
                        },
                        noAction: () => {
                        }
                    })
                    break
                case 3:
                    this.openDialog({
                        title: this.$t('user.disableUser'),
                        text: this.$t('user.disableUserText', { username: data.username }),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.disable([data])
                        },
                        noAction: () => {
                        }
                    })
                    break
                case 4:
                    this.isClone = true
                    this.loadFields(data._id)
                    break
                default:
                    console.log('default')
            }
        },
        /**
         * Click fuera del dialog
         */
        clickOutside() {
            this.cancel()
        },
        /**
         * Cancelar
         */
        cancel() {
            this.resetFields()
            this.showEditDialog = false
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            this.dialog.model = {
                username: null,
                fastAccess: false,
                comment: null,
                firstName: null,
                lastName: null,
                password: null,
                repeatPassword: null,
                email: null,
                enabled: true,
                dateRestriction: false,
                sinceDate: null,
                toDate: null,
                insideUser: false,
                isExternal: false,
                remoteAccess: false,
                modifyPasswordNextLogIn: false,
                passwordExpiration: false,
                passwordExpirationDays: 0,
                inactivityDisconnection: false,
                authorizedIpRange: false,
                ipRangeFrom: null,
                ipRangeTo: null,
                userThings: [],
                userEnterprises: [],
                userGroups: [],
                profileId: null,
                enterprises: [],
                update: false,
            }
        },
        /**
         * Abre el dialog para creación
         */
        showCreateDialog() {
            this.initBread()
            this.dialog.model.update = false
            this.editDialogTitle= i18n.t('user.editDialog.titleNew')
            this.editDialogIcon = 'mdi-account-plus'
            this.showEditDialog = true
        },
        /**
         * Abre el dialog para actualización
         */
        showUpdateDialog() {
            this.initBread()
            if (this.isClone) {
                this.dialog.model.username = undefined
                this.dialog.model.password = undefined
                this.dialog.model.repeatPassword = undefined
                this.dialog.model.firstName = undefined
                this.dialog.model.lastName = undefined
                this.dialog.model.email = undefined
                this.dialog.model.update = false
                this.editDialogTitle= i18n.t('user.editDialog.titleNew')
                this.editDialogIcon = 'fa-clone'
                this.showEditDialog = true
            } else {
                this.dialog.model.password = undefined
                this.dialog.model.repeatPassword = undefined
                this.dialog.model.update = true
                this.editDialogTitle= i18n.t('user.editDialog.titleUpdate')
                this.editDialogIcon = 'mdi-account-edit'
                this.showEditDialog = true
            }
        },
        /**
         * Paginador externo
         * @param {*} pageSize
         * @param {*} pageNumber
         */
        externalPaginator(pageSize, pageNumber) {
            this.pageNumber = Number(pageNumber)
            this.pageSize = Number(pageSize)
        },
        /**
         * Para deshabilitar usuarios
         * @param {*} ids
         */
        disable(data) {
            userService.disable(data.map(user => user._id)).then(() => {
                this.showSnackbar({
                    visible: true,
                    text: this.$t(data.length > 1 ? 'user.successDisableMessage' : 'user.successDisableUserMessage'),
                    timeout: 6000,
                    style: SnackbarStyle.SUCCESS
                })
                this.getData(true)
            })
        },
        /**
         * Snackbar de advertencia para funcionalidades que aún no se implementaron
         */
        notImplemented() {
            this.showSnackbar({
                visible: true,
                text: this.$t('notImplemented'),
                timeout: 6000,
                style: SnackbarStyle.WARNING
            })
        },
        enterpriseSelectedFunction (newValue) {
            this.enterpriseSelected = newValue._id ? newValue._id : newValue;
            this.getData(false)
        },
    },
}
